// useCurrentWorkspace — read-only handle to the signed-in buyer's
// default workspace (first row from `listWorkspaces`) hydrated via
// `getWorkspaceFull`. Used by the Explore Refine view and the
// Picked-for-you section, which both need workspace-derived state
// (prefs, id) without owning the bootstrap themselves.
//
// Shape contract is deliberately narrow so anonymous + signed-in
// callers handle the same return type:
//
//   - `workspace` is `null` when the user is unauthenticated, has no
//     workspaces yet, or the fetch failed. Callers degrade rather than
//     surfacing an error — e.g. Refine falls back to localStorage.
//   - `loading` is `true` only during the initial fetch. After it
//     resolves once, `loading` stays `false` even across `refresh()`
//     re-fetches so the UI doesn't flicker.
//   - `refresh()` re-runs list+full (e.g. after a mutation lands).
//
// Does NOT create-if-missing — that's intent-driven and lives in
// Workspace.tsx's bootstrap (landing on /workspace implies "I want
// one"). Buyer surfaces using this hook should keep working without a
// workspace.

import { useEffect, useRef, useState } from "react";
import {
  getWorkspaceFull,
  listWorkspaces,
  type WsFull,
} from "@/lib/workspaceApi";

export interface UseCurrentWorkspaceResult {
  workspace: WsFull | null;
  loading: boolean;
  refresh: () => void;
}

export function useCurrentWorkspace(): UseCurrentWorkspaceResult {
  const [workspace, setWorkspace] = useState<WsFull | null>(null);
  // Initial fetch only — `refresh()` re-runs without flipping loading
  // back to true so consumers don't show a spinner over already-rendered
  // content during a refetch.
  const [loading, setLoading] = useState(true);
  const [tick, setTick] = useState(0);
  const everResolved = useRef(false);

  useEffect(() => {
    let cancelled = false;
    if (!everResolved.current) setLoading(true);
    (async () => {
      try {
        const list = await listWorkspaces();
        if (list.length === 0) {
          if (!cancelled) {
            setWorkspace(null);
          }
          return;
        }
        const full = await getWorkspaceFull(list[0].id);
        if (!cancelled) {
          setWorkspace(full);
        }
      } catch {
        // 401 (anonymous) and network failures both degrade to "no
        // workspace" so the caller renders its fallback. The hook is
        // intentionally silent on errors — callers that need to
        // distinguish should fetch directly.
        if (!cancelled) {
          setWorkspace(null);
        }
      } finally {
        if (!cancelled) {
          everResolved.current = true;
          setLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [tick]);

  return { workspace, loading, refresh: () => setTick((n) => n + 1) };
}
